import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Same Cloud Function the marketing site's checkout.html posts to — calling
// it directly means a signed-in user never has to leave the app to pay.
const CHECKOUT_FUNCTION_URL = 'https://us-central1-familybibleapp.cloudfunctions.net/createCheckoutSession';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Try the experience with your closest circle.',
    features: [
      'Up to 3 family members',
      'Record up to 10 chapters of the Bible',
      'Verse-by-verse recording & playback',
      'WEB & BSB translations',
    ],
  },
  {
    id: 'legacy',
    name: 'Family Legacy',
    price: '$89',
    period: '/ year',
    description: 'The sweet spot for households building a complete family audio Bible.',
    features: [
      'Up to 5 family members',
      'Unlimited recording across the full Bible',
      'Full family audio library',
      'Guided recording & listening plans',
      'Family study notes & shareable links',
      'Email digests when family records',
      'Badges, streaks & progress tracking',
    ],
    featured: true,
  },
  {
    id: 'plus',
    name: 'Family+',
    price: '$149',
    period: '/ year',
    description: 'For extended families spanning households.',
    features: [
      'Up to 15 family members',
      'Multi-household support',
      'Everything in Family Legacy',
      'Ideal for grandparents & adult children',
    ],
  },
];

function CheckIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Subscription() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, refreshUserData } = useAuth();
  const [choosingPlanId, setChoosingPlanId] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fid = currentUser?.familyIds?.[0];
    if (!fid) return;
    getDoc(doc(db, 'families', fid)).then(snap => {
      if (snap.exists()) setFamilyName(snap.data()?.name || '');
    }).catch(() => {});
  }, [currentUser]);

  // Coming back from Stripe Checkout. The webhook that actually updates
  // subscriptionPlan/subscriptionStatus runs async, so refresh a couple of
  // times to catch it landing rather than showing stale "Free Plan" state.
  const checkoutResult = searchParams.get('checkout');
  useEffect(() => {
    if (checkoutResult !== 'success') return;
    refreshUserData?.();
    const retry = setTimeout(() => refreshUserData?.(), 3000);
    return () => clearTimeout(retry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult]);

  const dismissCheckoutBanner = () => {
    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Please Sign In</h2>
          <button onClick={() => navigate('/auth')} className="btn-primary">Sign In</button>
        </div>
      </div>
    );
  }

  const rawPlan = currentUser?.subscriptionPlan || 'free';
  const isBeta = currentUser?.isBeta === true;
  const subscriptionStatus = currentUser?.subscriptionStatus;
  const hasActivePaidPlan = !isBeta && subscriptionStatus === 'active' && rawPlan !== 'free';

  // Resolve display plan — beta/active users get full access; we show their
  // underlying plan either way.
  const activePlanId = (isBeta || hasActivePaidPlan) ? (rawPlan === 'free' ? 'legacy' : rawPlan) : rawPlan;

  // Choosing a plan card: go straight to Stripe Checkout, hosted, right from
  // the app — no detour through the marketing site for a signed-in user.
  const handleChoosePlan = async (plan) => {
    setError('');
    setChoosingPlanId(plan.id);
    try {
      const successUrl = `${window.location.origin}/subscription?checkout=success`;
      const cancelUrl = `${window.location.origin}/subscription?checkout=cancelled`;
      const res = await fetch(CHECKOUT_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan.id,
          email: currentUser.email || undefined,
          successUrl,
          cancelUrl,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      if (!data.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e) {
      console.error('Subscription: checkout error', e);
      setError('Could not start checkout. Please try again in a moment.');
      setChoosingPlanId(null);
    }
  };

  // No Stripe Customer Portal exists yet — an already-subscribed user still
  // has to go to the website to change payment method or cancel.
  const handleManageExisting = async () => {
    setManageLoading(true);
    const identityParams = new URLSearchParams();
    if (currentUser.email) identityParams.set('em', currentUser.email);
    const firstName = currentUser.firstName || (currentUser.displayName || '').split(' ')[0] || '';
    const lastName = currentUser.lastName || (currentUser.displayName || '').split(' ').slice(1).join(' ') || '';
    if (firstName) identityParams.set('dn', firstName);
    if (lastName) identityParams.set('ln', lastName);
    if (familyName) identityParams.set('fn', familyName);

    const newTab = window.open('', '_blank');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fns = getFunctions(getApp());
      const createToken = httpsCallable(fns, 'createCustomToken');
      const { data } = await createToken();
      identityParams.set('token', data.token);
      const url = `https://lineagebible.com/pricing.html?${identityParams}`;
      if (newTab) newTab.location.href = url; else window.location.href = url;
    } catch (e) {
      const url = `https://lineagebible.com/pricing.html?${identityParams}`;
      if (newTab) newTab.location.href = url; else window.location.href = url;
    } finally {
      setManageLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-28">
      {/* Back */}
      <button
        onClick={() => navigate('/profile')}
        className="text-cerulean hover:text-cerulean/80 text-sm font-medium mb-6 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Profile
      </button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {familyName ? `Plans for the ${familyName}` : 'Subscription'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">Choose a plan and pay right here — no need to leave the app.</p>
      </div>

      {/* Checkout result banners */}
      {checkoutResult === 'success' && (
        <div className="mb-6 rounded-2xl bg-green-50 border border-green-200 p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <div className="font-semibold text-sm text-green-800">Payment received!</div>
            <div className="text-xs text-green-700 mt-0.5">Finishing up your subscription — this can take a few seconds to appear below.</div>
          </div>
          <button onClick={dismissCheckoutBanner} className="text-green-400 hover:text-green-600 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {checkoutResult === 'cancelled' && (
        <div className="mb-6 rounded-2xl bg-gray-100 p-4 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">Checkout was cancelled — no charge was made.</span>
          <button onClick={dismissCheckoutBanner} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 border border-red-200 p-4 text-xs text-red-700">{error}</div>
      )}

      {/* Beta / current plan banner */}
      {isBeta ? (
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-cerulean to-aqua-deep p-4 text-white flex items-center gap-3">
          <span className="text-2xl">✦</span>
          <div>
            <div className="font-semibold text-sm">Friends Access</div>
            <div className="text-xs text-white/80">Full Access - Friends and Developers</div>
          </div>
        </div>
      ) : hasActivePaidPlan ? (
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-cerulean to-aqua-deep p-4 text-white flex items-center gap-3">
          <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <div className="font-semibold text-sm">Active subscription</div>
            <div className="text-xs text-white/80">
              {PLANS.find(p => p.id === rawPlan)?.name || rawPlan} plan
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 rounded-2xl bg-gray-100 p-4 flex items-center gap-3">
          <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <div>
            <div className="font-semibold text-sm text-gray-900">Free Plan</div>
            <div className="text-xs text-gray-500">Choose a plan below to unlock more.</div>
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="space-y-4 mb-8">
        {PLANS.map(plan => {
          const isCurrent = plan.id === activePlanId;
          // Friends Access accounts never pay — their subscription is
          // provisioned server-side (with the comp coupon) by a super admin,
          // not through this self-serve checkout, which has no idea about
          // that coupon and would charge full price. No self-serve
          // downgrade-to-free without a billing portal either — the Free
          // card is just informational once someone has a real plan.
          const canChoose = !isBeta && !isCurrent && !(plan.id === 'free' && hasActivePaidPlan);
          return (
            <div
              key={plan.id}
              className={`rounded-2xl border-2 p-5 transition-all ${
                isCurrent
                  ? 'border-cerulean bg-cerulean/5'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{plan.name}</span>
                    {plan.featured && (
                      <span className="text-xs font-semibold bg-cerulean text-white px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    )}
                    {isCurrent && (
                      <span className="text-xs font-semibold bg-cerulean/15 text-cerulean px-2 py-0.5 rounded-full">
                        {isBeta ? 'Friends' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{plan.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-xs text-gray-400 ml-1">{plan.period}</span>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {plan.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className={isCurrent ? 'text-cerulean' : 'text-gray-400'}>
                      <CheckIcon />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              {canChoose && (
                <button
                  onClick={() => handleChoosePlan(plan)}
                  disabled={choosingPlanId !== null}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold text-sm shadow-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {choosingPlanId === plan.id ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      Redirecting to checkout…
                    </>
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {isBeta && (
        <p className="text-center text-xs text-gray-400 mb-4">
          Friends Access is comped by the team — no payment needed.
        </p>
      )}

      {/* Manage an existing paid subscription (no in-app billing portal yet) */}
      {hasActivePaidPlan && (
        <button
          onClick={handleManageExisting}
          disabled={manageLoading}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          {manageLoading ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
              Opening…
            </>
          ) : (
            <>
              Change payment method or cancel
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </>
          )}
        </button>
      )}

      <p className="text-center text-xs text-gray-400 mt-4">
        Payments are securely processed by Stripe.
      </p>
    </div>
  );
}

export default Subscription;
